import {t} from '@lingui/macro'
import {useEffect, useState} from 'react'

import {bffHooks} from '../bff-hooks'
import {hasAuthUrls, isPandiumOrigin, isPandiumSuccess} from '../lib/validation-helper'

type IntegrationsGatewayAuth = LoadingURLs | Idle | Started | Error | Connected

type LoadingURLs = {
    connectionStatus: 'LOADING_URLS'
}

type Idle = {
    connectionStatus: 'IDLE'
    startConnection: () => void
}

type Started = {
    connectionStatus: 'STARTED'
}

type Error = {
    connectionStatus: 'ERROR'
    cause: string
}

type Connected = {
    connectionStatus: 'CONNECTED'
}

/**
 * Custom hook to manage the authentication process with the Integrations Gateway.
 *
 * @param {string} appIdentifier - The identifier for the application, usually the
 * @returns {IntegrationsGatewayAuth} - An object containing the state and function to manage the authentication process.
 *
 * @property {() => void} startConnection - Function to initiate the connection process.
 * @property {ConnectionStatus} connectionStatus - The current status of the connection.
 */
export function useIntegrationsGatewayAuth(appIdentifier: string): IntegrationsGatewayAuth {
    const {
        data: authUrls,
        isLoading: isLoadingURLs,
        isError: isErrorLoadingURLs,
        error: errorLoadingURLs,
    } = bffHooks.getAuthURLs.useQuery(appIdentifier)
    const {mutateAsync: createInstallationTenant} = bffHooks.createInstallationTenant.useMutation()

    const [connectionStatus, setConnectionStatus] =
        useState<IntegrationsGatewayAuth['connectionStatus']>('LOADING_URLS')

    const [targetTab, setTargetTab] = useState<Window | null>(null)

    const startConnection = () => {
        if (connectionStatus === 'STARTED') {
            return
        }

        if (hasAuthUrls(authUrls)) {
            setConnectionStatus('STARTED')

            const tab = window.open(authUrls.pleoAuthUrl, '_blank')
            setTargetTab(tab)
        }
    }

    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            if (targetTab === null || authUrls === undefined) {
                return
            }

            if (
                isPandiumOrigin(event.origin) &&
                isPandiumSuccess(event.data.message) &&
                event.source === targetTab
            ) {
                const tenantAuthStatus = event.data.tenant.status.auth

                if (
                    !tenantAuthStatus.connected &&
                    tenantAuthStatus.PLEO_AUTH_STATUS === 'Connected'
                ) {
                    // first auth (Pleo) is completed, redirect to target system auth
                    targetTab.location.replace(authUrls.targetSystemAuthUrl)

                    return
                }

                if (tenantAuthStatus.connected) {
                    // second auth (target system) is completed, tenant is `connected`, and installation tenant is created
                    targetTab.close()
                    setTargetTab(null)
                    await createInstallationTenant({
                        applicationSlug: appIdentifier,
                        tenantMetaData: {data: event.data},
                    })
                    setConnectionStatus('CONNECTED')
                    return
                }
            }
        }

        window.addEventListener('message', handleMessage)

        return () => {
            window.removeEventListener('message', handleMessage)
        }
    }, [authUrls, targetTab])

    useEffect(() => {
        if (isLoadingURLs) {
            return
        }

        if (isErrorLoadingURLs) {
            setConnectionStatus('ERROR')
            return
        }

        setConnectionStatus('IDLE')
    }, [isErrorLoadingURLs, isLoadingURLs])

    if (connectionStatus === 'ERROR') {
        return {
            connectionStatus,
            cause: errorLoadingURLs?.message ?? t`Something went wrong`,
        }
    }

    if (connectionStatus === 'IDLE') {
        return {connectionStatus, startConnection}
    }

    return {
        connectionStatus,
    }
}
